import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Container, Typography, Box, Button, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, Tooltip, CircularProgress, Dialog,
    DialogTitle, DialogContent, DialogActions, TextField, Stack, Card, CardContent, Grid, Divider, InputAdornment, Chip
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as VisibilityIcon, ContentCopy as ContentCopyIcon, Check as CheckIcon } from '@mui/icons-material';
import { getAllItineraryNotes, createItineraryNote, updateItineraryNote, deleteItineraryNote } from '../../api/itineraryStoreAPI';
import useSnackbar from '../../hooks/useSnackbar';
import usePermissions from '../../hooks/UsePermissions';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import parse from 'html-react-parser';

// Preserve all formatting on paste
const quillModules = {
    toolbar: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['link'],
        ['clean'],
    ],
    clipboard: {
        matchVisual: false, // keeps formatting intact when pasting
    },
};

const quillFormats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'link',
];

const ItineraryStore = () => {
    const { showSnackbar, SnackbarComponent } = useSnackbar();
    const hasPermission = usePermissions();

    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(false);

    // Dialog State
    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    const [currentNote, setCurrentNote] = useState(null);
    const [copied, setCopied] = useState(false);
    const viewContentRef = useRef(null);
    const [formData, setFormData] = useState({ 
        title: '', 
        itinerary: [{ headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] }] 
    });

    const fetchNotes = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getAllItineraryNotes();
            if (res.success) {
                setNotes(res.data);
            }
        } catch (error) {
            console.error("Error fetching notes:", error);
            showSnackbar("Failed to fetch itineraries", "error");
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        fetchNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenDialog = (note = null) => {
        if (note) {
            setCurrentNote(note);
            setFormData({ 
                title: note.title, 
                itinerary: note.itinerary && note.itinerary.length > 0 ? note.itinerary : [{ headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] }] 
            });
        } else {
            setCurrentNote(null);
            setFormData({ title: '', itinerary: [{ headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] }] });
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setCurrentNote(null);
        setFormData({ title: '', itinerary: [{ headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] }] });
    };

    const handleSave = async () => {
        if (!formData.title.trim()) {
            showSnackbar("Title is required", "warning");
            return;
        }

        const dataToSave = {
            ...formData,
            itinerary: formData.itinerary
        };

        try {
            if (currentNote) {
                await updateItineraryNote(currentNote._id, dataToSave);
                showSnackbar("Itinerary updated successfully", "success");
            } else {
                await createItineraryNote(dataToSave);
                showSnackbar("Itinerary created successfully", "success");
            }
            handleCloseDialog();
            fetchNotes();
        } catch (error) {
            console.error("Error saving note:", error);
            showSnackbar(error.response?.data?.message || "Failed to save itinerary", "error");
        }
    };

    const handleOpenView = (note) => {
        setCurrentNote(note);
        setViewDialogOpen(true);
    };

    const handleOpenDelete = (note) => {
        setCurrentNote(note);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!currentNote) return;
        try {
            await deleteItineraryNote(currentNote._id);
            showSnackbar("Itinerary deleted successfully", "success");
            setDeleteDialogOpen(false);
            fetchNotes();
        } catch (error) {
            console.error("Error deleting note:", error);
            showSnackbar("Failed to delete itinerary", "error");
        }
    };

    // Itinerary Builder Functions
    const addDay = () => {
        setFormData({
            ...formData,
            itinerary: [...formData.itinerary, { headerTag: '', headerValue: '', Distance: '', Duration: '', Altitude: '', Time: '', para: [''] }]
        });
    };

    const removeDay = (index) => {
        const newItinerary = [...formData.itinerary];
        newItinerary.splice(index, 1);
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleItineraryChange = (index, field, value) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[index][field] = value;
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const addParagraph = (dayIndex) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para.push('');
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const removeParagraph = (dayIndex, paraIndex) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para.splice(paraIndex, 1);
        setFormData({ ...formData, itinerary: newItinerary });
    };

    const handleCopyItinerary = async () => {
        if (!currentNote || !viewContentRef.current) return;
        try {
            // Generate clean, semantic HTML with explicit inline styles
            // This ensures formatting (bold, bullets, fonts) is preserved in Word, Email, etc.
            let htmlContent = `<div style="font-family: Arial, Helvetica, sans-serif; color: #0f172a;">`;
            
            currentNote.itinerary?.forEach((day) => {
                htmlContent += `<div style="margin-bottom: 24px;">`;
                
                // Header (Title)
                const headerText = day.headerValue || '';
                htmlContent += `<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 12px; margin-top: 0; color: #1e293b;">${headerText}</h3>`;
                
                // Meta details (Distance, Duration, etc.)
                if (day.Distance || day.Duration || day.Altitude || day.Time) {
                    htmlContent += `<div style="font-size: 14px; margin-bottom: 12px; color: #475569;">`;
                    if (day.Distance) htmlContent += `<span style="margin-right: 16px;"><strong>Distance:</strong> ${day.Distance}</span>`;
                    if (day.Duration) htmlContent += `<span style="margin-right: 16px;"><strong>Duration:</strong> ${day.Duration}</span>`;
                    if (day.Altitude) htmlContent += `<span style="margin-right: 16px;"><strong>Altitude:</strong> ${day.Altitude}</span>`;
                    if (day.Time) htmlContent += `<span><strong>Time:</strong> ${day.Time}</span>`;
                    htmlContent += `</div>`;
                }
                
                // Paragraphs
                if (day.para && day.para.length > 0) {
                    day.para.forEach(p => {
                        if (p) htmlContent += `<div style="font-size: 15px; line-height: 1.6; margin-bottom: 12px;">${p}</div>`;
                    });
                }
                htmlContent += `</div>`;
            });
            htmlContent += `</div>`;

            const blob = new Blob([htmlContent], { type: "text/html" });
            const plainText = currentNote.itinerary?.map(d => `${d.headerValue || ''}\n${(d.para || []).join('\n')}`).join('\n\n') || '';
            const plainBlob = new Blob([plainText], { type: "text/plain" });
            
            const data = [new ClipboardItem({
                "text/html": blob,
                "text/plain": plainBlob
            })];

            await navigator.clipboard.write(data);
            showSnackbar("Full itinerary copied to clipboard!", "success");
        } catch (error) {
            console.error('Failed to copy:', error);
            showSnackbar("Failed to copy itinerary", "error");
        }
    };

    const handleCopySingleItinerary = async (day) => {
        try {
            let htmlContent = `<div style="font-family: Arial, Helvetica, sans-serif; color: #0f172a;">`;
            const headerText = day.headerValue || '';
            htmlContent += `<h3 style="font-size: 18px; font-weight: bold; margin-bottom: 12px; margin-top: 0; color: #1e293b;">${headerText}</h3>`;
            
            if (day.Distance || day.Duration || day.Altitude || day.Time) {
                htmlContent += `<div style="font-size: 14px; margin-bottom: 12px; color: #475569;">`;
                if (day.Distance) htmlContent += `<span style="margin-right: 16px;"><strong>Distance:</strong> ${day.Distance}</span>`;
                if (day.Duration) htmlContent += `<span style="margin-right: 16px;"><strong>Duration:</strong> ${day.Duration}</span>`;
                if (day.Altitude) htmlContent += `<span style="margin-right: 16px;"><strong>Altitude:</strong> ${day.Altitude}</span>`;
                if (day.Time) htmlContent += `<span><strong>Time:</strong> ${day.Time}</span>`;
                htmlContent += `</div>`;
            }

            if (day.para && day.para.length > 0) {
                day.para.forEach((p) => {
                    if (p) htmlContent += `<div style="font-size: 15px; line-height: 1.6; margin-bottom: 12px;">${p}</div>`;
                });
            }
            htmlContent += `</div>`;

            const blob = new Blob([htmlContent], { type: "text/html" });
            const plainText = `${day.headerValue || ''}\n${(day.para || []).join('\n')}`;
            const plainBlob = new Blob([plainText], { type: "text/plain" });
            
            const data = [new ClipboardItem({
                "text/html": blob,
                "text/plain": plainBlob
            })];

            await navigator.clipboard.write(data);
            showSnackbar("Day itinerary copied to clipboard!", "success");
        } catch (err) {
            console.error("Failed to copy day", err);
            showSnackbar("Failed to copy day itinerary", "error");
        }
    };

    const handleParagraphChange = (dayIndex, paraIndex, value) => {
        const newItinerary = [...formData.itinerary];
        newItinerary[dayIndex].para[paraIndex] = value;
        setFormData({ ...formData, itinerary: newItinerary });
    };

    return (
        <Container maxWidth="xl" sx={{ py: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" color="primary" fontWeight={800}>
                    Detailed Itinerary Store
                </Typography>
                {hasPermission("itinerary-store", "create") && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Add New Itinerary
                    </Button>
                )}
            </Box>

            <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 2 }}>
                <Table>
                    <TableHead sx={{ bgcolor: '#f1f5f9' }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created By</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Created At</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : notes.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                    No itineraries found in the store.
                                </TableCell>
                            </TableRow>
                        ) : (
                            notes.map((note) => (
                                <TableRow key={note._id} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{note.title}</TableCell>
                                    <TableCell>{note.created_by?.fullName || "Unknown"}</TableCell>
                                    <TableCell>{new Date(note.created_at).toLocaleDateString()}</TableCell>
                                    <TableCell align="right">
                                        <Tooltip title="View">
                                            <IconButton color="primary" onClick={() => handleOpenView(note)}>
                                                <VisibilityIcon />
                                            </IconButton>
                                        </Tooltip>
                                        {hasPermission("itinerary-store", "alter") && (
                                            <Tooltip title="Edit">
                                                <IconButton color="secondary" onClick={() => handleOpenDialog(note)}>
                                                    <EditIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        {hasPermission("itinerary-store", "delete") && (
                                            <Tooltip title="Delete">
                                                <IconButton color="error" onClick={() => handleOpenDelete(note)}>
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>{currentNote ? "Edit Itinerary" : "Create New Itinerary"}</DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            label="Itinerary Title"
                            fullWidth
                            variant="outlined"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="e.g. Gangtok 2N3D Itinerary"
                        />
                        
                        <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>Itinerary Days</Typography>
                        
                        {formData.itinerary.map((day, dayIndex) => (
                            <Card key={dayIndex} variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                                        <Button size="small" color="error" onClick={() => removeDay(dayIndex)}>Remove Day</Button>
                                    </Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={12}>
                                            <TextField 
                                                size="small" 
                                                fullWidth 
                                                label="Header Value" 
                                                value={day.headerValue} 
                                                onChange={(e) => handleItineraryChange(dayIndex, 'headerValue', e.target.value)} 
                                                placeholder="e.g. Arrival at Bagdogra – Transfer to Gangtok (125 km)" 
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField size="small" fullWidth label="Distance" value={day.Distance} onChange={(e) => handleItineraryChange(dayIndex, 'Distance', e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField size="small" fullWidth label="Duration" value={day.Duration} onChange={(e) => handleItineraryChange(dayIndex, 'Duration', e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField size="small" fullWidth label="Altitude" value={day.Altitude} onChange={(e) => handleItineraryChange(dayIndex, 'Altitude', e.target.value)} />
                                        </Grid>
                                        <Grid item xs={12} sm={3}>
                                            <TextField size="small" fullWidth label="Time" value={day.Time} onChange={(e) => handleItineraryChange(dayIndex, 'Time', e.target.value)} />
                                        </Grid>
                                    </Grid>
                                    
                                    <Box sx={{ mt: 3 }}>
                                        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>Paragraphs</Typography>
                                        {day.para.map((paragraph, paraIndex) => (
                                            <Box key={paraIndex} sx={{ display: 'flex', gap: 2, mb: 3 }}>
                                                <Box sx={{ flexGrow: 1, minHeight: '120px', '.ql-editor': { minHeight: '100px', maxHeight: '300px', overflowY: 'auto' } }}>
                                                    <Typography variant="caption" color="textSecondary" sx={{ mb: 0.5, display: 'block' }}>Paragraph {paraIndex + 1}</Typography>
                                                    <ReactQuill 
                                                        theme="snow" 
                                                        value={paragraph} 
                                                        onChange={(content) => handleParagraphChange(dayIndex, paraIndex, content)}
                                                        modules={quillModules}
                                                        formats={quillFormats}
                                                    />
                                                </Box>
                                                <IconButton color="error" onClick={() => removeParagraph(dayIndex, paraIndex)} sx={{ alignSelf: 'flex-start', mt: 3 }}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        ))}
                                        <Button size="small" variant="outlined" onClick={() => addParagraph(dayIndex)} sx={{ mt: 1 }}>Add Paragraph</Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        ))}
                        
                        <Button variant="dashed" sx={{ border: '1px dashed #ccc', py: 2 }} onClick={addDay} fullWidth>
                            + ADD ANOTHER DAY
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={handleCloseDialog} color="inherit">Cancel</Button>
                    <Button onClick={handleSave} variant="contained" color="primary">Save Itinerary</Button>
                </DialogActions>
            </Dialog>

            {/* View Dialog */}
            <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
                <DialogTitle sx={{ bgcolor: '#f8fafc', fontWeight: 700, color: '#0f172a' }}>
                    {currentNote?.title}
                </DialogTitle>
                <DialogContent dividers sx={{ bgcolor: '#fff', p: 0 }}>
                    <Box ref={viewContentRef} sx={{ p: 2, bgcolor: '#f8fafc' }}>
                        <Grid container spacing={2}>
                            {currentNote?.itinerary?.map((day, index) => (
                                <Grid item xs={12} key={index}>
                                    <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, bgcolor: '#ffffff', position: 'relative', display: 'flex', flexDirection: 'column' }}>
                                        <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', pr: 4 }}>
                                                    {day.headerValue}
                                                </Typography>
                                                <Tooltip title="Copy this day">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleCopySingleItinerary(day)}
                                                        sx={{ 
                                                            position: 'absolute', 
                                                            top: 16, 
                                                            right: 16,
                                                            color: '#3b82f6',
                                                            bgcolor: '#eff6ff',
                                                            '&:hover': { bgcolor: '#dbeafe' }
                                                        }}
                                                    >
                                                        <ContentCopyIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Box>
                                            
                                            {(day.Distance || day.Duration || day.Altitude || day.Time) && (
                                                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                                                    {day.Distance && <Chip size="small" label={`Dist: ${day.Distance}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Duration && <Chip size="small" label={`Dur: ${day.Duration}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Altitude && <Chip size="small" label={`Alt: ${day.Altitude}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                    {day.Time && <Chip size="small" label={`Time: ${day.Time}`} sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />}
                                                </Box>
                                            )}
                                            
                                            {day.para && day.para.length > 0 && (
                                                <Box sx={{ color: '#475569', fontSize: '0.95rem' }}>
                                                    {day.para.map((p, i) => (
                                                        <Box key={i} dangerouslySetInnerHTML={{ __html: p }} sx={{ mb: i !== day.para.length - 1 ? 1.5 : 0 }} />
                                                    ))}
                                                </Box>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, justifyContent: 'flex-end' }}>
                    <Button onClick={() => setViewDialogOpen(false)} color="inherit" sx={{ fontWeight: 600 }}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete the itinerary "{currentNote?.title}"?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained">Delete</Button>
                </DialogActions>
            </Dialog>

            <SnackbarComponent />
        </Container>
    );
};

export default ItineraryStore;
